const CURRENT_MEMBERSHIP_STATUSES = new Set(["active", "trialing"]);
const FULL_MEMBER_STATUSES = new Set(["active"]);
const PAST_DUE_GRACE_PERIOD = 14 * 24 * 60 * 60 * 1000;

const DEFAULT_MEMBERSHIP_ACCESS = {
  state: "none",
  canAccessMemberContent: false,
  canUseFreeRace: false,
  canUseMemberRaceDiscount: false,
  showPaymentWarning: false,
  showTrialingNotice: false,
  accessEndsAt: null,
  subscription: null,
};

export function getMembershipDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function getMembershipTime(value) {
  const date = getMembershipDate(value);
  return date ? date.getTime() : null;
}

function normalizeStatus(subscription) {
  return typeof subscription?.status === "string" ? subscription.status.toLowerCase() : "";
}

function hasScheduledCancellation(subscription) {
  return Boolean(subscription?.cancel_at_period_end || subscription?.cancel_at);
}

function getAccessEndTimes(subscription, status = normalizeStatus(subscription)) {
  const accessEndValues = [
    subscription?.cancel_at,
    subscription?.current_period_end,
  ];
  if (status === "trialing") {
    accessEndValues.push(subscription?.trial_end);
  }

  return accessEndValues
    .map(getMembershipTime)
    .filter((time) => Number.isFinite(time));
}

function isUnexpiredMembershipSubscription(subscription, nowMs = Date.now()) {
  const status = normalizeStatus(subscription);
  if (!CURRENT_MEMBERSHIP_STATUSES.has(status)) {
    return false;
  }

  const accessEndTimes = getAccessEndTimes(subscription, status);
  if (accessEndTimes.length === 0) {
    return true;
  }

  return Math.min(...accessEndTimes) > nowMs;
}

function getLatestSubscription(subscriptions, predicate = () => true) {
  if (!Array.isArray(subscriptions)) {
    return null;
  }

  return subscriptions
    .filter((subscription) => subscription && predicate(subscription))
    .sort((a, b) => getMembershipSortTime(b) - getMembershipSortTime(a))[0] || null;
}

export function getMembershipSortTime(subscription) {
  const dateValues = [
    subscription?.canceled_at,
    subscription?.ended_at,
    subscription?.cancel_at,
    subscription?.current_period_end,
    subscription?.trial_end,
    subscription?.current_period_start,
    subscription?.created,
  ]
    .map((value) => getMembershipTime(value))
    .filter((value) => Number.isFinite(value));

  return dateValues.length > 0 ? Math.max(...dateValues) : 0;
}

function getPastDueGrace(subscription, membershipGrace, nowMs) {
  const storedSubscriptionId = membershipGrace?.subscriptionId || membershipGrace?.subscription_id || null;
  const matchesStoredSubscription =
    !storedSubscriptionId || !subscription?.id || storedSubscriptionId === subscription.id;
  const storedPastDueSince = matchesStoredSubscription ? getMembershipTime(membershipGrace?.pastDueSince) : null;
  const storedGraceEndsAt = matchesStoredSubscription ? getMembershipTime(membershipGrace?.graceEndsAt) : null;
  const pastDueSince = Number.isFinite(storedPastDueSince) ? storedPastDueSince : nowMs;
  const graceEndsAt = Number.isFinite(storedGraceEndsAt)
    ? storedGraceEndsAt
    : pastDueSince + PAST_DUE_GRACE_PERIOD;

  return {
    pastDueSince,
    graceEndsAt,
  };
}

export function getMembershipAccess(subscriptions, membershipGrace = null, nowMs = Date.now()) {
  const fullMemberSubscription = getLatestSubscription(
    subscriptions,
    (subscription) =>
      FULL_MEMBER_STATUSES.has(normalizeStatus(subscription)) &&
      isUnexpiredMembershipSubscription(subscription, nowMs)
  );

  if (fullMemberSubscription) {
    const accessEndTimes = getAccessEndTimes(fullMemberSubscription);
    const accessEndsAt = accessEndTimes.length > 0 ? Math.min(...accessEndTimes) : null;

    return {
      ...DEFAULT_MEMBERSHIP_ACCESS,
      state: hasScheduledCancellation(fullMemberSubscription) ? "canceling_active" : "active",
      canAccessMemberContent: true,
      canUseFreeRace: true,
      canUseMemberRaceDiscount: true,
      accessEndsAt,
      subscription: fullMemberSubscription,
    };
  }

  const trialingSubscription = getLatestSubscription(
    subscriptions,
    (subscription) =>
      normalizeStatus(subscription) === "trialing" &&
      isUnexpiredMembershipSubscription(subscription, nowMs)
  );

  if (trialingSubscription) {
    const accessEndTimes = getAccessEndTimes(trialingSubscription, "trialing");
    const accessEndsAt = accessEndTimes.length > 0 ? Math.min(...accessEndTimes) : null;

    return {
      ...DEFAULT_MEMBERSHIP_ACCESS,
      state: "trialing",
      canAccessMemberContent: true,
      showTrialingNotice: true,
      accessEndsAt,
      subscription: trialingSubscription,
    };
  }

  const pastDueSubscription = getLatestSubscription(
    subscriptions,
    (subscription) => normalizeStatus(subscription) === "past_due"
  );

  if (pastDueSubscription) {
    const grace = getPastDueGrace(pastDueSubscription, membershipGrace, nowMs);
    const isInsideGrace = grace.graceEndsAt > nowMs;

    return {
      ...DEFAULT_MEMBERSHIP_ACCESS,
      state: isInsideGrace ? "past_due_grace" : "past_due_expired",
      canAccessMemberContent: isInsideGrace,
      showPaymentWarning: true,
      accessEndsAt: grace.graceEndsAt,
      subscription: pastDueSubscription,
    };
  }

  const latestSubscription = getLatestSubscription(subscriptions);
  const latestStatus = normalizeStatus(latestSubscription);

  return {
    ...DEFAULT_MEMBERSHIP_ACCESS,
    state: latestStatus === "canceled" || latestStatus === "cancelled" ? "expired_canceled" : "none",
    subscription: latestSubscription,
  };
}

export function isCurrentMembershipSubscription(subscription, nowMs = Date.now()) {
  return isUnexpiredMembershipSubscription(subscription, nowMs);
}

export function hasCurrentMembershipSubscription(subscriptions, nowMs = Date.now()) {
  return getMembershipAccess(subscriptions, null, nowMs).canAccessMemberContent;
}
